import type { ReactNode } from 'react';
import { Dialog, DialogTrigger, Popover } from 'react-aria-components';
import cx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { IconDotsOutlined, IconFiltersOutlined, IconPlusOutlined } from '@repo/monochrome-icons';
import { Button } from '../../button';
import { DropdownButton } from '../../dropdown-button';
import { Spinner } from '../../spinner';
import styles from './new-widget.module.css';

interface NewWidgetProps {
  title: string;
  children?: ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onRemove?: () => void;
  className?: string;
  contentPadding?: string | number;
  filterSlot?: ReactNode;
}

interface FiltersProps {
  children: ReactNode;
  hasActiveFilter?: boolean;
}

function Filters({ children, hasActiveFilter = false }: FiltersProps): ReactNode {
  const { formatMessage } = useIntl();

  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;

  const label = formatMessage({
    id: 'design-system-kit.modular-home.new-widget.filter-button.label',
  });

  const filterByLabel = formatMessage({
    id: 'design-system-kit.modular-home.new-widget.filter-panel.label',
  });

  return (
    <DialogTrigger>
      <Button
        aria-label={label}
        className={styles.filterButton}
        iconOnly
        size="small"
        variant="tertiary"
      >
        <IconFiltersOutlined height={16} width={16} />
        {hasActiveFilter ? (
          <span
            aria-hidden="true"
            className={styles.filterIndicator}
            data-testid="active-filter-dot"
          />
        ) : null}
      </Button>
      <Popover
        UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
        className={styles.filterPopover}
        offset={8}
        placement="bottom start"
        shouldFlip
      >
        <Dialog aria-label={filterByLabel} className={styles.filterDialog}>
          <span className={cx('caption', styles.filterByLabel)}>{filterByLabel}</span>
          {children}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

interface FiltersContentProps {
  children: ReactNode;
}

function FiltersContent({ children }: FiltersContentProps): ReactNode {
  return <div className={styles.filterContent}>{children}</div>;
}

function FilterSeparator(): ReactNode {
  return <hr className={styles.filterSeparator} />;
}

interface AdvancedFiltersButtonProps {
  onPress?: () => void;
  label?: string;
  icon?: ReactNode;
}

function AdvancedFiltersButton({
  onPress,
  label,
  icon = <IconPlusOutlined />,
}: AdvancedFiltersButtonProps): ReactNode {
  const { formatMessage } = useIntl();

  return (
    <Button className={styles.advancedFiltersButton} onPress={onPress} stretch variant="secondary">
      <div className={styles.buttonContentWrapper}>
        {icon}
        {label
          ? label
          : formatMessage({
              id: 'design-system-kit.modular-home.new-widget.advanced-filters-button.label',
            })}
      </div>
    </Button>
  );
}

function NewWidgetComponent({
  title,
  children,
  isLoading,
  isError,
  onRetry,
  onRemove,
  className,
  contentPadding = '16px',
  filterSlot,
}: NewWidgetProps): ReactNode {
  const { formatMessage } = useIntl();

  const menuLabel = formatMessage({
    id: 'design-system-kit.modular-home.new-widget.remove-button.label',
  });
  const removeLabel = formatMessage({
    id: 'design-system-kit.modular-home.new-widget.remove-menu-item.label',
  });

  const renderContent = (): ReactNode => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <Spinner size="medium" />
        </div>
      );
    }

    if (isError) {
      return (
        <div className={styles.errorContainer}>
          <p className="body-standard">
            <FormattedMessage id="design-system-kit.modular-home.new-widget.loading-failed-title.label" />
          </p>
          {onRetry ? (
            <Button onPress={onRetry} size="small" variant="tertiary">
              <FormattedMessage id="design-system-kit.modular-home.new-widget.try-again-cta.label" />
            </Button>
          ) : null}
        </div>
      );
    }

    return children;
  };

  return (
    <div className={cx(styles.wrapper, className)}>
      <div className={styles.header}>
        <h4 className={cx('body-standard', styles.title)}>{title}</h4>
        {(filterSlot ?? onRemove) ? (
          <div className={styles.headerActions}>
            {filterSlot}
            {onRemove ? (
              <DropdownButton
                iconOnly
                items={[
                  {
                    label: removeLabel,
                    onSelect: onRemove,
                    variant: 'destructive',
                  },
                ]}
                tooltipAriaLabel={menuLabel}
                tooltipSize="small"
                variant="secondary"
              >
                <IconDotsOutlined height={16} width={16} />
              </DropdownButton>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className={styles.content} style={{ padding: contentPadding }}>
        {renderContent()}
      </div>
    </div>
  );
}

export const NewWidget = Object.assign(NewWidgetComponent, {
  Filters,
  FiltersContent,
  FilterSeparator,
  AdvancedFiltersButton,
});
